import { useState } from 'react';

import type { Booking, BookingGarment, BookingWearer, PrintLetterhead } from '@/api/bookings';
import type { TaxDocument } from '@/api/taxDocuments';
import { Card } from '@/components/PageHeading';
import { formatDate, formatDateTime } from '@core/format/dates';
import { formatAmount, formatSigned } from '@core/format/money';
import { MoneyLine, MoneyLines } from './MoneyLine';
import { humanizeEnum } from '@core/format/text';
import type { BookingPayment } from '@/api/bookings';
import type { DocketLanguage } from '@/api/generated/enums';
import { TaxDocumentDrawer } from '@/pages/invoices/TaxDocumentDrawer';
import { SheetPrint } from '@/pages/print/SheetPrint';
import { docketPage, docketSheets } from '@core/print/docketHtml';
import { fittingCardPage, fittingCards } from '@core/print/fittingCardHtml';
import { invoiceSheet, receiptSheet } from '@core/print/sheetHtml';
import { DocketLanguageChooser } from './DocketLanguageChooser';
import { recoveryLines } from '@core/bookings/charges';
import { bookingHistory, tabLabel, whenChip, type DetailTab } from '@core/bookings/detailTabs';
import { formatPct } from '@core/bookings/discount';
import type { StageMove } from '@core/bookings/stageMoves';
import { documentTitle } from '@core/invoices/register';
import { PartyTable } from './PartyTable';
import { chargeableDays } from '@core/bookings/pricing';
import { referenceLabel } from '@core/bookings/reference';
import { StageTracker } from './StageTracker';
import { stageDates } from '@core/bookings/stages';
import { BookingMenu } from './BookingMenu';

const tabs: readonly DetailTab[] = ['garments', 'money', 'history'];

/**
 * One booking, laid out as the phone lays it out (owner, 2026-10-22; the canvas's "Booking on the
 * web") — option B of the phone's 2026-10-19 canvas, on a wide screen.
 *
 * <p>
 * <strong>The band says when; the tabs say what needs looking at.</strong> Across the top: the
 * reference and stage, who, the occasion, the chip that says when (`whenChip`), Call, Print and
 * ··· More. Under it, Garments · Money · History as tabs whose labels carry what is waiting
 * (`@core/bookings/detailTabs`), and on the right <strong>the one thing that happens next</strong>
 * (`@core/bookings/nextStep`) with the quieter moves under it. Invoices, receipts, printing and the
 * ways out are in the ··· menu (`BookingMenu`), so Cancel is never beside the big button.
 * </p>
 *
 * <p>
 * It stays a rendering of a booking: the next step, the quieter moves and the ways out are the
 * page's, passed in, so this has no session or mutation in sight. <strong>The deposit is never inside
 * the invoice total</strong>; it has its own block under a rule.
 * </p>
 */
export function BookingDetail({
  booking,
  letterhead,
  onBack,
  currency = '',
  today,
  prints = true,
  readsInvoices = false,
  next,
  actions,
  notice,
  exits = [],
  mayExtend = false,
  onExit,
  onExtend,
  onMoveGarment,
  onViewPhotos,
  onShowHistory,
  onFitting,
}: {
  booking: Booking;
  /** Absent until the letterhead loads; printing is offered only once it is there. */
  letterhead: PrintLetterhead | undefined;
  onBack: () => void;
  currency?: string;
  /** The shop's date, for the band's chip and the history's "to come". */
  today: string;
  /** `RentalOrder:Print` — the customer copy and the dockets. */
  prints?: boolean;
  /** Billing with `SalesInvoice:View` — the invoices in the menu. */
  readsInvoices?: boolean;
  /** What happens next, with its button — the page's. */
  next?: React.ReactNode;
  /** The quieter moves under it — the page's. */
  actions?: React.ReactNode;
  /** Said above the tabs — the late-return cascade. */
  notice?: React.ReactNode;
  /** The ways out this reader may take, for the menu. */
  exits?: readonly StageMove[];
  /** Keep it longer, for the menu — only while something is out and the reader may edit. */
  mayExtend?: boolean;
  onExit?: ((move: StageMove) => void) | undefined;
  onExtend?: (() => void) | undefined;
  /** Raised with the garment whose fulfilment state is being moved. */
  onMoveGarment?: ((garment: BookingGarment) => void) | undefined;
  /** Raised with the garment whose pictures are being opened. */
  onViewPhotos?: ((garment: BookingGarment) => void) | undefined;
  /** Opens the garment timeline off a row. The parent owns the panel, as it owns every overlay. */
  onShowHistory?: ((garment: BookingGarment) => void) | undefined;
  /** Raised with the wearer whose fitting is being booked, moved or marked done. */
  onFitting?: ((wearer: BookingWearer) => void) | undefined;
}) {
  /**
   * Which document is on screen, and for what. `garmentId` is set when a clerk asked for one
   * garment's docket off its own row; absent means every garment's, one page each.
   */
  const [printing, setPrinting] = useState<
    | { doc: 'customer' }
    | { doc: 'receipt'; payment: BookingPayment }
    | { doc: 'docket'; garmentId?: number }
    | { doc: 'card' }
    | { doc: 'invoice'; document: TaxDocument }
    | null
  >(null);
  // Fixed when the page opens, so the preview and the print say the same time.
  const [printedAt] = useState(() => new Date().toISOString());
  const [tab, setTab] = useState<DetailTab>('garments');
  const [menuOpen, setMenuOpen] = useState(false);
  const [invoice, setInvoice] = useState<TaxDocument | null>(null);

  // An override for this print only, on top of whatever the tailor's vendor record says.
  const [docketLanguage, setDocketLanguage] = useState<DocketLanguage | undefined>(undefined);

  const hasDocket = letterhead ? docketSheets({ booking, letterhead }).length > 0 : false;
  const hasCard = fittingCards({ booking }).length > 0;
  const when = whenChip(booking, today);

  /*
    Every print is the phone's own page (`@core/print/*`), on the phone's papers (owner,
    2026-10-22): the summary, a receipt and an invoice on all four; the docket and the fitting card
    on A5 alone, as the phone prints them.
  */
  if (printing && letterhead) {
    const eyebrow = `${referenceLabel(booking)} · ${booking.customerName}`;
    const close = () => setPrinting(null);

    switch (printing.doc) {
      case 'customer':
        return (
          <SheetPrint
            eyebrow={eyebrow}
            title="Booking summary"
            remember="receipt"
            build={(paper, rollLength) =>
              receiptSheet({ booking, letterhead, payment: null, printedAt }, paper, rollLength)
            }
            onClose={close}
          />
        );
      case 'receipt':
        return (
          <SheetPrint
            eyebrow={eyebrow}
            title={`Receipt ${printing.payment.receiptNumber}`}
            remember="receipt"
            build={(paper, rollLength) =>
              receiptSheet(
                { booking, letterhead, payment: printing.payment, printedAt },
                paper,
                rollLength,
              )
            }
            onClose={close}
          />
        );
      case 'invoice':
        return (
          <SheetPrint
            eyebrow={eyebrow}
            title={`${documentTitle(printing.document)} ${printing.document.documentNumber}`}
            remember="invoice"
            build={(paper, rollLength) =>
              invoiceSheet(
                { document: printing.document, letterhead, printedAt },
                paper,
                rollLength,
              )
            }
            onClose={close}
          />
        );
      case 'docket':
        return (
          <SheetPrint
            eyebrow={eyebrow}
            title="Alteration docket"
            papers={['A5']}
            extra={
              <DocketLanguageChooser
                booking={booking}
                garmentId={printing.garmentId}
                override={docketLanguage}
                onOverride={setDocketLanguage}
              />
            }
            build={() =>
              docketPage({
                booking,
                letterhead,
                garmentId: printing.garmentId,
                language: docketLanguage,
              })
            }
            onClose={close}
          />
        );
      case 'card':
        return (
          <SheetPrint
            eyebrow={eyebrow}
            title="Fitting card"
            papers={['A5']}
            build={() => fittingCardPage({ booking, letterhead })}
            onClose={close}
          />
        );
    }
  }

  const occasion = [booking.eventKind, booking.eventDate ? formatDate(booking.eventDate) : null]
    .filter(Boolean)
    .join(' · ');
  const bandButton =
    'btn-sm inline-flex cursor-pointer items-center gap-2 rounded border border-white/40 text-white hover:bg-white/15 disabled:opacity-50';

  return (
    <>
      {/* ------------------------------------------------------------------ the band */}
      <div className="bg-primary mb-4 flex flex-wrap items-center gap-x-5 gap-y-3 rounded-md px-4 py-3.5 text-white">
        <button
          type="button"
          onClick={onBack}
          data-shortcut="back"
          aria-label="Back to bookings"
          className="flex size-8 cursor-pointer items-center justify-center rounded hover:bg-white/15"
        >
          <i className="icon icon-arrow-left text-[18px]" />
        </button>

        <div className="min-w-0">
          <p className="text-[11px] tracking-wide text-white/80 uppercase">
            {referenceLabel(booking)} · {humanizeEnum(booking.stage)}
          </p>
          <h1 className="truncate text-xl font-bold text-white">{booking.customerName}</h1>
          <p className="text-[13px] text-white/85">
            {occasion ||
              `${booking.wearerCount} wearer${booking.wearerCount === 1 ? '' : 's'}, ${booking.garmentCount} garment${booking.garmentCount === 1 ? '' : 's'}`}
          </p>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {when && (
            <span
              className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${
                when.tone === 'danger'
                  ? 'text-danger bg-white'
                  : when.tone === 'warning'
                    ? 'text-warning bg-white'
                    : 'bg-white/20 text-white'
              }`}
            >
              {when.text}
            </span>
          )}
          <span className="rounded-full bg-white/20 px-2.5 py-1 text-[12px] font-semibold text-white">
            {formatDate(booking.possessionFromDate)} → {formatDate(booking.possessionToDate)}
          </span>
          {booking.holdExpiresOn && !booking.isHoldExpired && (
            <span className="rounded-full bg-white/20 px-2.5 py-1 text-[12px] font-semibold text-white">
              Hold ends {formatDateTime(booking.holdExpiresOn)}
            </span>
          )}
        </div>

        <div className="ms-auto flex flex-wrap gap-2">
          {booking.customerMobile && (
            <a
              href={`tel:${booking.customerMobile}`}
              aria-label={`Call ${booking.customerName}`}
              className={bandButton}
            >
              <i className="icon icon-phone" />
              {booking.customerMobile}
            </a>
          )}
          {/* The customer copy takes Alt+P: it is the paper printed on every booking. */}
          {prints && (
            <button
              type="button"
              data-shortcut="print"
              disabled={!letterhead}
              onClick={() => setPrinting({ doc: 'customer' })}
              className={bandButton}
            >
              <i className="icon icon-printer" />
              Print
            </button>
          )}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="More for this booking"
            className={bandButton}
          >
            <i className="icon icon-ellipsis" />
            More
          </button>
        </div>
      </div>

      {notice}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* -------------------------------------------------------------- the tabs */}
        <div className="min-w-0">
          <div role="tablist" className="border-border-color mb-3 flex gap-6 border-b">
            {tabs.map((each) => (
              <button
                key={each}
                type="button"
                role="tab"
                aria-selected={tab === each}
                onClick={() => setTab(each)}
                className={`-mb-px cursor-pointer border-b-2 px-0.5 py-2.5 text-[14px] font-semibold ${
                  tab === each ? 'border-primary text-primary' : 'text-default border-transparent'
                }`}
              >
                {tabLabel(each, booking, currency)}
              </button>
            ))}
          </div>

          {tab === 'garments' && (
            <Card flush>
              <PartyTable
                payerName={booking.customerName}
                wearers={booking.wearers}
                currency={currency}
                onMoveGarment={onMoveGarment}
                onViewPhotos={onViewPhotos}
                onShowHistory={onShowHistory}
                onFitting={onFitting}
                onPrintDocket={(garment) => setPrinting({ doc: 'docket', garmentId: garment.id })}
              />
            </Card>
          )}

          {tab === 'money' && (
            <div className="grid gap-4 xl:grid-cols-2">
              <Money booking={booking} currency={currency} />
              <Receipts booking={booking} currency={currency} />
            </div>
          )}

          {tab === 'history' && (
            <div className="flex flex-col gap-4">
              <Card>
                <StageTracker stage={booking.stage} dates={stageDates(booking)} />
              </Card>
              <History booking={booking} today={today} currency={currency} />
            </div>
          )}
        </div>

        {/* --------------------------------------------------- what happens next */}
        <div className="flex flex-col gap-4">
          {next}
          {actions}
          <Dates booking={booking} />
        </div>
      </div>

      {menuOpen && (
        <BookingMenu
          booking={booking}
          currency={currency}
          prints={prints && letterhead !== undefined}
          readsInvoices={readsInvoices}
          hasDocket={hasDocket}
          hasCard={hasCard}
          onPrintCard={() => setPrinting({ doc: 'card' })}
          onPrintReceipt={(payment) => setPrinting({ doc: 'receipt', payment })}
          exits={exits}
          mayExtend={mayExtend}
          onPrintCopy={() => setPrinting({ doc: 'customer' })}
          onPrintDockets={() => setPrinting({ doc: 'docket' })}
          onOpenInvoice={setInvoice}
          onExtend={() => onExtend?.()}
          onExit={(move) => onExit?.(move)}
          onClose={() => setMenuOpen(false)}
        />
      )}

      {invoice && (
        <TaxDocumentDrawer
          document={invoice}
          onClose={() => setInvoice(null)}
          onPrint={() => {
            setPrinting({ doc: 'invoice', document: invoice });
            setInvoice(null);
          }}
        />
      )}
    </>
  );
}

/** The booking's story in date order — the shared `bookingHistory`, the phone's History tab. */
function History({
  booking,
  today,
  currency,
}: {
  booking: Booking;
  today: string;
  currency: string;
}) {
  const entries = bookingHistory(booking, today, currency);

  return (
    <Card>
      {entries.length === 0 ? (
        <p className="text-default text-[12.5px]">Nothing recorded yet.</p>
      ) : (
        <ol className="flex list-none flex-col ps-0">
          {entries.map((entry, index) => (
            <li
              key={`${entry.day}-${index}`}
              className="border-border-color flex list-none items-baseline gap-4 border-t py-2 first:border-t-0"
            >
              <span className="text-default w-24 shrink-0 text-[12px]">
                {formatDate(entry.day)}
              </span>
              <span
                className={`text-[13px] ${entry.upcoming ? 'text-default italic' : 'text-gray-900'}`}
              >
                {entry.text}
              </span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

/**
 * Three date pairs, because they genuinely differ.
 *
 * The customer is charged for the event days, the shop blocks the round trip, and the garment is
 * only back on the rail after its cleaning buffer. One pair doing all three jobs gets two of them
 * wrong — either the customer is overcharged or the calendar promises stock that is out of the
 * shop — so the screen shows all three rather than letting anyone assume they are the same.
 */
function Dates({ booking }: { booking: Booking }) {
  return (
    <Card flush>
      <dl className="grid grid-cols-2">
        <Fact label="Event date" value={formatDate(booking.eventDate)} note={booking.eventKind} />
        <Fact
          label="Charged period"
          value={`${formatDate(booking.chargeFromDate)} – ${formatDate(booking.chargeToDate)}`}
          note={`${chargeableDays(booking.chargeFromDate, booking.chargeToDate)} days`}
        />
        <Fact
          label="Possession"
          value={`${formatDate(booking.possessionFromDate)} – ${formatDate(
            booking.possessionToDate,
          )}`}
          note={`${chargeableDays(booking.possessionFromDate, booking.possessionToDate)} days blocked`}
        />
        <Fact
          label="Back on rail"
          value={formatDate(booking.freeAgainOn)}
          note="after turnaround buffer"
        />
      </dl>
    </Card>
  );
}

function Fact({ label, value, note }: { label: string; value: string; note?: string | null }) {
  return (
    <div className="sm:border-border-color px-4 py-3 sm:border-e sm:last:border-e-0">
      <dt className="text-default text-[10.5px] font-semibold tracking-wider uppercase">{label}</dt>
      <dd className="m-0 mt-1 text-sm font-medium text-gray-900">
        {value}
        {note && <span className="text-default block text-[11.5px] font-normal">{note}</span>}
      </dd>
    </div>
  );
}

/**
 * The money.
 *
 * <p>
 * <strong>The deposit sits below a rule, outside the invoice total.</strong> A deposit is not a
 * supply — excluded from taxable value and taxed only if forfeited — so a total that folded it in
 * would charge GST on money the shop is merely holding. Laying it out so that adding it in would
 * look wrong is a cheap way to stop an expensive error.
 * </p>
 */
function Money({ booking, currency }: { booking: Booking; currency: string }) {
  const { deposit } = booking;

  return (
    <Card>
      <p className="mb-2.5 text-sm font-semibold text-gray-900">Money</p>

      <MoneyLines>
        <MoneyLine label="Rental" value={`${currency}${formatAmount(booking.rentalAmount)}`} />

        {/* Its own line, never folded into the rental above it. A concession the customer cannot see
            is one the shop gets no credit for and cannot answer a question about, and the reason
            beside it is what makes the line auditable rather than merely visible.

            The two levels are kept apart where both exist, because "₹500 off the sherwani" and "10%
            off the bill" are two things the customer was told and one combined figure answers
            neither. The reason hangs off the last line, which is the one that completes the story. */}
        {booking.discount.lineAmount ? (
          <MoneyLine
            label="Garment discounts"
            value={`− ${currency}${formatAmount(booking.discount.lineAmount)}`}
            tone="brand"
          />
        ) : null}

        {booking.discount.amount ? (
          <MoneyLine
            label="Discount on the booking"
            value={`− ${currency}${formatAmount(booking.discount.amount)}`}
            tone="brand"
          />
        ) : null}

        {booking.discount.totalAmount > 0 ? (
          <MoneyLine
            label={`Total off · ${formatPct(booking.discount.effectivePct)}%`}
            value={`− ${currency}${formatAmount(booking.discount.totalAmount)}`}
            tone="brand"
            note={
              booking.discount.approvedByName
                ? `${booking.discount.reason ?? ''} — ${booking.discount.approvedByName}`
                : booking.discount.reason
            }
          />
        ) : null}

        {/* Named, and with the days it bought, because "why is this 3,600 more than the rental"
            is the question a clerk gets asked with the customer standing there. It sits above the
            tax for the reason the alteration does: everything that makes up the taxable value is
            listed before the tax on it. */}
        {booking.extensionAmount ? (
          <MoneyLine
            label={`Kept longer · ${booking.extensionDays} ${
              booking.extensionDays === 1 ? 'day' : 'days'
            }`}
            value={`${currency}${formatAmount(booking.extensionAmount)}`}
          />
        ) : null}

        {booking.alterationAmount ? (
          <MoneyLine
            label="Alteration"
            value={`${currency}${formatAmount(booking.alterationAmount)}`}
          />
        ) : null}

        {/* What the return assessed — named rather than absorbed into the total, which is how a
            settled booking came to show charge lines that did not add up to it. `warning`, because
            these are the customer's to answer for and a clerk reads them as such. */}
        {recoveryLines(booking).map((line) => (
          <MoneyLine
            key={line.key}
            label={line.label}
            value={`${currency}${formatAmount(line.amount)}`}
            tone="warning"
          />
        ))}
        {booking.taxAmount ? (
          <MoneyLine label="GST" value={`${currency}${formatAmount(booking.taxAmount)}`} />
        ) : null}
        <MoneyLine
          label="Invoice total"
          value={`${currency}${formatAmount(booking.invoiceTotal)}`}
          total
        />
        <MoneyLine label="Received" value={`${currency}${formatAmount(booking.paidAmount)}`} />
        <MoneyLine
          label="Balance"
          value={`${currency}${formatAmount(booking.balanceDue)}`}
          tone={booking.balanceDue > 0 ? 'warning' : undefined}
        />
      </MoneyLines>

      <div className="border-border-color mt-3 border-t pt-3">
        <MoneyLines>
          <MoneyLine
            label={`Deposit${deposit.multiplier ? ` · ${deposit.multiplier}×` : ''}`}
            value={`${currency}${formatAmount(deposit.amount)}`}
            tone="brand"
          />
          <MoneyLine label="State" value={humanizeEnum(deposit.state)} />
        </MoneyLines>
        <p className="text-default mt-2 text-[11.5px] leading-relaxed">
          Not a supply — excluded from taxable value, taxed only if forfeited. Taken{' '}
          {depositPoint(deposit.collectionPoint)}, refunded {refundPoint(deposit.refundPoint)}.
        </p>
      </div>
    </Card>
  );
}

function Receipts({ booking, currency }: { booking: Booking; currency: string }) {
  return (
    <Card>
      <p className="mb-2.5 text-sm font-semibold text-gray-900">Receipts</p>

      {booking.payments.length === 0 ? (
        <p className="text-default text-[12.5px]">Nothing received yet.</p>
      ) : (
        <ul className="divide-border-color divide-y">
          {booking.payments.map((payment) => (
            <li key={payment.id} className="flex items-baseline justify-between gap-3 py-2">
              <span className="min-w-0">
                <span className="block font-mono text-[11.5px] text-gray-900">
                  {payment.receiptNumber}
                </span>
                <span className="text-default block text-[11px]">
                  {formatDate(payment.receivedOn)} · {humanizeEnum(payment.method)} ·{' '}
                  {humanizeEnum(payment.kind)}
                </span>
              </span>
              {/* Signed, so a refund reads as one rather than sitting beside a receipt of the
                  same size looking identical. */}
              <span className="font-mono-nums text-[13px] font-medium text-gray-900">
                {currency}
                {formatSigned(payment.amount)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** When each stage was reached, for the tracker. */

function depositPoint(point: Booking['deposit']['collectionPoint']): string {
  return point === 'AtBooking' ? 'at booking' : point === 'AtPickup' ? 'at pickup' : 'in two parts';
}

function refundPoint(point: Booking['deposit']['refundPoint']): string {
  return point === 'AtCounter'
    ? 'at the counter'
    : point === 'AfterInspection'
      ? 'after inspection'
      : 'after cleaning';
}
